import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Mic, Zap, Clock, Smartphone, Lock, Youtube, Headphones } from "lucide-react";

const features = [
  {
    title: "Audio Recording",
    description: "Record your live lectures and meetings directly in the app. Zuto automatically transcribes and generates structured notes from your audio recordings.",
    icon: Mic
  },
  {
    title: "YouTube Integration",
    description: "Paste any YouTube link and Zuto will extract the audio, transcribe it, and generate comprehensive notes. Perfect for online courses and tutorials.",
    icon: Youtube
  },
  {
    title: "AI-Powered Notes",
    description: "Get clear, well-structured notes automatically generated from recordings and YouTube videos. Organized into three sections: Notes, Revise, and Q&A.",
    icon: Zap
  },
  {
    title: "Smart Revision",
    description: "Quickly revisit content with a scrollable feed of highlights, explanations, and additional context to strengthen your understanding.",
    icon: Clock
  },
  {
    title: "Interactive Q&A",
    description: "Test yourself with AI-generated questions and answers based on your recordings and videos to reinforce memory and prepare for exams.",
    icon: Headphones
  },
  {
    title: "Cross-Platform Sync",
    description: "Works across devices with secure cloud sync, so your study materials are always accessible wherever you are.",
    icon: Smartphone
  }
];

const FeaturesSection = () => {
  return (
    <section id="features" className="py-20 bg-background px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Why Choose <span className="text-primary">Zuto</span>?
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Transform any audio source into structured study material. Record live lectures or convert YouTube videos into comprehensive notes.
            </p>
          </motion.div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="bg-card border-border h-full hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="mb-2 text-primary bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center">
                      <Icon size={24} />
                    </div>
                    <CardTitle className="text-card-foreground">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
